import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, Method } from "axios";
import { setupInterceptors } from "./interceptors";

// ----------------------
// Dynamic Environment getters - ALL environment variables
// ----------------------
const getBaseUrl = () => import.meta.env.VITE_API_BASE_URL || "http://localhost:5000";
const getApiTimeout = () => Number(import.meta.env.VITE_API_TIMEOUT) || 3000;
const getLogsEnabled = () => import.meta.env.VITE_ENABLE_LOGS === 'true';
const getAppName = () => import.meta.env.VITE_APP_NAME || "Salon App";
const getAppVersion = () => import.meta.env.VITE_APP_VERSION || "1.0.0";
const getDebugMode = () => import.meta.env.VITE_DEBUG_MODE === 'true';
const getMockApi = () => import.meta.env.VITE_MOCK_API === 'true';
const getShowEnvBanner = () => import.meta.env.VITE_SHOW_ENV_BANNER === 'true';
const getAppEnv = () => import.meta.env.VITE_APP_ENV || 'development';

// ----------------------
// Dynamic Default headers from environment - ALL headers included
// ----------------------
const getDefaultHeaders = () => {
  const headers: Record<string, string> = {};
  
  // Content-Type (with fallback)
  const contentType = import.meta.env.VITE_DEFAULT_CONTENT_TYPE || "application/json";
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  
  // Accept header (optional)
  const accept = import.meta.env.VITE_DEFAULT_ACCEPT;
  if (accept) {
    headers["Accept"] = accept;
  }
  
  // User-Agent header (optional)
  const userAgent = import.meta.env.VITE_DEFAULT_USER_AGENT;
  if (userAgent) {
    // headers["User-Agent"] = userAgent;
  }
  
  // API Version header (optional)
  const apiVersion = import.meta.env.VITE_DEFAULT_API_VERSION;
  if (apiVersion) {
    // headers["X-API-Version"] = apiVersion;
  }
  
  // Client Type header (optional)
  const clientType = import.meta.env.VITE_DEFAULT_CLIENT_TYPE;
  if (clientType) {
    // headers["X-Client-Type"] = clientType;
  }
  
  // App Name header (dynamic from env)
  const appName = getAppName();
  if (appName) {
    // headers["X-App-Name"] = appName;
  }
  
  // App Version header (dynamic from env)
  const appVersion = getAppVersion();
  if (appVersion) {
    // headers["X-App-Version"] = appVersion;
  }
  
  // Environment header (dynamic from env)
  const environment = getAppEnv();
  if (environment) {
    // headers["X-Environment"] = environment;
  }
  
  return headers;
};

// ----------------------
// Create minimal Axios instance (no static config)
// ----------------------
const api: AxiosInstance = axios.create({
  // No static configuration - all dynamic values applied per-request
});

// ----------------------
// Setup interceptors
// ----------------------
setupInterceptors(api);

// ----------------------
// Generic request function
// ----------------------
export interface RequestOptions extends Omit<AxiosRequestConfig, 'method'> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  skipAuth?: boolean;
}

export const request = async <TResponse = any>({
  method = "GET",
  url,
  data,
  params,
  headers,
  timeout,
  skipAuth = true,
  ...config
}: RequestOptions): Promise<TResponse> => {
  // Apply all dynamic environment values per-request
  
  // Get dynamic baseURL
  const baseURL = getBaseUrl();
  
  // Merge default headers with custom headers (both dynamic)
  const mergedHeaders = {
    ...getDefaultHeaders(),
    ...headers,
  };
  
  // Use custom timeout or fallback to dynamic default
  const requestTimeout = timeout || getApiTimeout();
  
  const requestConfig: AxiosRequestConfig & { skipAuth?: boolean } = {
    method: method as Method,
    baseURL,                    // Dynamic baseURL applied per-request
    url,
    data,
    params,
    headers: mergedHeaders,     // Dynamic headers applied per-request
    timeout: requestTimeout,    // Dynamic timeout applied per-request
    skipAuth,
    ...config,
  };
  
  const response = await api.request<TResponse>(requestConfig);
  return response.data;
};

// ----------------------
// Shortcut methods
// ----------------------
export const Api = {
  get: async <T = any>(
    url: string,
    params?: Record<string, any>,
    headers?: Record<string, string>,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'params' | 'headers'>
  ): Promise<T> => {
    return request<T>({ method: "GET", url, params, headers, ...config });
  },

  post: async <T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'headers'>
  ): Promise<T> => {
    return request<T>({ method: "POST", url, data, headers, ...config });
  },

  put: async <T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'headers'>
  ): Promise<T> => {
    return request<T>({ method: "PUT", url, data, headers, ...config });
  },

  patch: async <T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'headers'>
  ): Promise<T> => {
    return request<T>({ method: "PATCH", url, data, headers, ...config });
  },

  delete: async <T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>,
    config?: Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'headers'>
  ): Promise<T> => {
    return request<T>({ method: "DELETE", url, data, headers, ...config });
  },
};

// ----------------------
// Utility to get ALL API and Environment info dynamically
// ----------------------
export const getApiInfo = () => ({
  // API Configuration
  baseUrl: getBaseUrl(),
  timeout: getApiTimeout(),
  logsEnabled: getLogsEnabled(),
  
  // App Information
  appName: getAppName(),
  appVersion: getAppVersion(),
  environment: getAppEnv(),
  
  // Feature Flags
  debugMode: getDebugMode(),
  mockApi: getMockApi(),
  showEnvBanner: getShowEnvBanner(),
  
  // Headers Preview
  defaultHeaders: getDefaultHeaders(),
});

// ----------------------
// Additional utility functions for easy access
// ----------------------
export const getEnvironmentConfig = () => ({
  environment: getAppEnv(),
  debugMode: getDebugMode(),
  logsEnabled: getLogsEnabled(),
  mockApi: getMockApi(),
  showEnvBanner: getShowEnvBanner(),
});

export const getAppConfig = () => ({
  name: getAppName(),
  version: getAppVersion(),
  environment: getAppEnv(),
});

export default api;
